import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Alert, Pagination, Spinner, Modal, Nav } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import ReportChatAssistant from '../components/ReportChatAssistant';
import DetectionJourneyModal from '../components/DetectionJourneyModal';
import { faceImageAPI, classroomAPI, childAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import type { FaceImageRecord, FaceImageFilters, ChildAggregate, ChildrenAggregateFilters } from '../types/faceimage';
import type { Classroom } from '../types/classroom';
import type { Child } from '../types/child';
import { formatDateTimeInNurseryTimezone, getTodayDateInNurseryTimezone } from '../utils/timezone';

import ScopeIndicator from '../components/ScopeIndicator';

type ViewMode = 'individual' | 'summary';

type JourneyChildSelection = {
  childId: number;
  childName: string;
};

// Helper function to get today's date in YYYY-MM-DD format for nursery timezone
const getTodayDate = (nurseryTimezone: string = 'UTC'): string => {
  return getTodayDateInNurseryTimezone(nurseryTimezone);
};

const isHolidayAggregate = (child: ChildAggregate) => {
  const value = child.is_holiday;
  return value === true || value === 'true' || value === '1' || value === 'Holiday' || value === 'holiday';
};

// Custom hook for optimized filter management
const useOptimizedFilters = (initialFilters: FaceImageFilters, nurseryTimezone: string) => {
  const [filters, setFilters] = useState<FaceImageFilters>(initialFilters);
  const [formState, setFormState] = useState({
    childName: '',
    classroomName: '',
    fromDate: getTodayDate(nurseryTimezone),
    toDate: getTodayDate(nurseryTimezone),
    timezone: nurseryTimezone // Use nursery timezone
  });

  // Update form state when filters change
  useEffect(() => {
    setFormState({
      childName: filters.child_name || '',
      classroomName: filters.classroom_name || '',
      fromDate: filters.from_date || '',
      toDate: filters.to_date || '',
      timezone: nurseryTimezone // Use nursery timezone
    });
  }, [filters.child_name, filters.classroom_name, filters.from_date, filters.to_date, nurseryTimezone]);

  const updateFilters = useCallback((newFilters: Partial<FaceImageFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const updateFiltersWithPageReset = useCallback((newFilters: Partial<FaceImageFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  }, []);

  const updateFormState = useCallback((field: string, value: string) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  }, []);

  const applyFormFilters = useCallback(() => {
    const newFilters: Partial<FaceImageFilters> = {
      child_name: formState.childName.trim() || undefined,
      classroom_name: formState.classroomName.trim() || undefined,
      from_date: formState.fromDate || undefined,
      to_date: formState.toDate || undefined,
      page: 1
    };
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, [formState]);

  const clearFilters = useCallback(() => {
    const today = getTodayDate(nurseryTimezone);
    const clearedFilters: FaceImageFilters = {
      per_page: 20,
      page: 1,
      from_date: today,
      to_date: today
    };
    setFilters(clearedFilters);
    setFormState({
      childName: '',
      classroomName: '',
      fromDate: today,
      toDate: today,
      timezone: nurseryTimezone
    });
  }, [nurseryTimezone, setFilters, setFormState]);

  // Stable setFilters function
  const stableSetFilters = useCallback((updater: React.SetStateAction<FaceImageFilters>) => {
    setFilters(updater);
  }, []);

  return {
    filters,
    formState,
    updateFilters,
    updateFiltersWithPageReset,
    updateFormState,
    applyFormFilters,
    clearFilters,
    setFilters: stableSetFilters,
    setFormState
  };
};

// Custom hook for data loading
const useDataLoader = (currentNursery: any) => {
  const { t } = useTranslation('pages', { keyPrefix: 'reports' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);

  const loadIndividualRecords = useCallback(async (
    filters: FaceImageFilters,
    setFaceImages: (data: FaceImageRecord[]) => void,
    setCurrentPage: (page: number) => void,
    setTotalPages: (pages: number) => void,
    setTotalRecords: (records: number) => void,
    bypassCache: boolean = false
  ) => {
    if (!currentNursery?.nursery_id || loadingRef.current) {
      console.log('⚠️ Skipping loadIndividualRecords:', { 
        nurseryId: currentNursery?.nursery_id, 
        loadingRefCurrent: loadingRef.current 
      });
      return;
    }

    console.log('🚀 Starting loadIndividualRecords with:', { 
      filters, 
      bypassCache, 
      nurseryId: currentNursery.nursery_id 
    });

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await faceImageAPI.getFaceImages(currentNursery.nursery_id, filters, bypassCache);
      console.log('✅ API response received:', { 
        success: response.success, 
        dataLength: response.data?.length,
        currentPage: response.current_page,
        totalPages: response.last_page,
        total: response.total
      });
      
      if (response.success) {
        setFaceImages(response.data);
        setCurrentPage(response.current_page);
        setTotalPages(response.last_page);
        setTotalRecords(response.total);
        console.log('✅ Individual records loaded successfully:', response.data.length, 'records');
      } else {
        console.error('❌ API returned success=false');
        setError(t('loadFaceImages'));
      }
    } catch (err: any) {
      console.error('❌ Error loading individual records:', err);
      setError(err.response?.data?.message || t('loadFaceRecords'));
    } finally {
      setLoading(false);
      loadingRef.current = false;
      console.log('🏁 loadIndividualRecords completed');
    }
  }, [currentNursery?.nursery_id]);

  const loadChildFirstLastRecords = useCallback(async (
    childName: string,
    otherFilters: Partial<FaceImageFilters>,
    setFirstLastRecords: (data: { first?: FaceImageRecord, last?: FaceImageRecord }) => void
  ) => {
    if (!currentNursery?.nursery_id || !childName.trim()) {
      setFirstLastRecords({});
      return;
    }

    try {
      // Create base filters for this child
      const baseFilters = {
        child_name: childName.trim(),
        classroom_name: otherFilters.classroom_name,
        camera_name: otherFilters.camera_name,
        camera_number: otherFilters.camera_number,
        image_file: otherFilters.image_file,
        model_setup: otherFilters.model_setup,
        from_date: otherFilters.from_date,
        to_date: otherFilters.to_date,
        per_page: 1, // Only get 1 record per call for efficiency
        page: 1
      };

      // Make two parallel API calls to get first and last records efficiently
      const [firstResponse, lastResponse] = await Promise.all([
        // Get the earliest record (oldest first, page 1)
        faceImageAPI.getFaceImages(currentNursery.nursery_id, {
          ...baseFilters,
          // Note: We'll rely on the default API sorting (usually by date DESC)
          // and get the last page to find the oldest record
        }),
        // Get the most recent record (newest first, page 1) 
        faceImageAPI.getFaceImages(currentNursery.nursery_id, {
          ...baseFilters,
          per_page: 1,
          page: 1 // This should give us the most recent record
        })
      ]);

      let firstRecord: FaceImageRecord | undefined;
      let lastRecord: FaceImageRecord | undefined;

      // Get the most recent record (should be first in default sorting)
      if (lastResponse.success && lastResponse.data.length > 0) {
        lastRecord = lastResponse.data[0];
      }

      // To get the oldest record, we need to go to the last page
      if (firstResponse.success && firstResponse.total > 0) {
        const totalPages = firstResponse.last_page || Math.ceil(firstResponse.total / 1);
        
        if (totalPages > 1) {
          // Get the last page to find the oldest record
          try {
            const oldestResponse = await faceImageAPI.getFaceImages(currentNursery.nursery_id, {
              ...baseFilters,
              per_page: 1,
              page: totalPages
            });
            
            if (oldestResponse.success && oldestResponse.data.length > 0) {
              firstRecord = oldestResponse.data[0];
            }
          } catch (oldestError) {
            console.warn('Failed to load oldest record, using fallback:', oldestError);
            // Fallback: use the same record as last if we can't get the oldest
            firstRecord = lastRecord;
          }
        } else {
          // Only one page, so first and last are the same
          firstRecord = lastRecord;
        }
      }

      // Set the results
      if (firstRecord && lastRecord && firstRecord.log_time !== lastRecord.log_time) {
        // We have different first and last records
        setFirstLastRecords({ first: firstRecord, last: lastRecord });
      } else if (lastRecord) {
        // Only one record or first/last are the same
        setFirstLastRecords({ first: lastRecord });
      } else {
        setFirstLastRecords({});
      }

    } catch (err: any) {
      console.error('Error loading first/last records:', err);
      // Graceful fallback - don't show first/last records on error
      setFirstLastRecords({});
    }
  }, [currentNursery?.nursery_id]);

  const loadChildrenSummary = useCallback(async (
    filters: ChildrenAggregateFilters,
    setChildrenSummary: (data: ChildAggregate[]) => void,
    setSummaryTotalRecords: (records: number) => void,
    setSummaryTotalPages: (pages: number) => void,
    setSummaryPage: (page: number) => void,
    itemsPerPage: number,
    bypassCache: boolean = false
  ) => {
    if (!currentNursery?.nursery_id || loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await faceImageAPI.getChildrenAggregate(currentNursery.nursery_id, filters, bypassCache);
      if (response.success) {
        setChildrenSummary(response.data);
        setSummaryTotalRecords(response.data.length);
        setSummaryTotalPages(Math.ceil(response.data.length / itemsPerPage));
        setSummaryPage(1);
      } else {
        setError(t('loadSummary'));
      }
    } catch (err: any) {
      console.error('Error loading child summary:', err);
      setError(err.response?.data?.message || t('loadSummary'));
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [currentNursery?.nursery_id]);

  return { loading, error, loadIndividualRecords, loadChildFirstLastRecords, loadChildrenSummary };
};

const ReportsComponent: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'reports' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const location = useLocation();
  
  // Data state
  const [faceImages, setFaceImages] = useState<FaceImageRecord[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // First/Last records state - NEW
  const [firstLastRecords, setFirstLastRecords] = useState<{ first?: FaceImageRecord, last?: FaceImageRecord }>({});
  
  // Child summary state
  const [childrenSummary, setChildrenSummary] = useState<ChildAggregate[]>([]);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryTotalPages, setSummaryTotalPages] = useState(0);
  const [summaryTotalRecords, setSummaryTotalRecords] = useState(0);
  const [expandedChildren, setExpandedChildren] = useState<Set<string>>(new Set());
  
  // UI state
  const [viewMode, setViewMode] = useState<ViewMode>('summary');
  const [showImageModal, setShowImageModal] = useState(false);
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [selectedJourneyChild, setSelectedJourneyChild] = useState<JourneyChildSelection | null>(null);
  const [journeyError, setJourneyError] = useState<string | null>(null);
  const [journeyResolving, setJourneyResolving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<FaceImageRecord | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  
  // Share functionality state
  const [loadingSharedRecord, setLoadingSharedRecord] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  
  // CSV Export state
  const [csvExporting, setCsvExporting] = useState(false);
  
  // Cache bypass state - for fresh data when Apply Filters is clicked
  const [bypassCache, setBypassCache] = useState(false);
  
  // Navigation state - track if we're navigating from Child Timeline
  const [isNavigatingFromTimeline, setIsNavigatingFromTimeline] = useState(false);
  
  // URL processing state - track when URL parameters have been processed
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
  
  // Classrooms state
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [classroomsLoading, setClassroomsLoading] = useState(false);
  
  // Children state
  const [children, setChildren] = useState<Child[]>([]);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Get nursery timezone with fallback
  const nurseryTimezone = currentNursery?.timezone || 'UTC';

  // Constants
  const SUMMARY_ITEMS_PER_PAGE = 12;
  const INITIAL_FILTERS = { 
    per_page: 20, 
    page: 1,
    from_date: getTodayDate(nurseryTimezone),
    to_date: getTodayDate(nurseryTimezone)
  };

  // Custom hooks
  const {
    filters,
    formState,
    updateFilters,
    updateFiltersWithPageReset,
    updateFormState,
    applyFormFilters,
    clearFilters,
    setFilters,
    setFormState
  } = useOptimizedFilters(INITIAL_FILTERS, nurseryTimezone);

  const { loading, error, loadIndividualRecords, loadChildFirstLastRecords, loadChildrenSummary } = useDataLoader(currentNursery);

  const prevNurseryIdRef = useRef<string | undefined>(currentNursery?.nursery_id);

  // Format date and time using nursery timezone
  const formatDateTime = (dateString: string): string => {
    if (!dateString) return 'N/A';
    return formatDateTimeInNurseryTimezone(dateString, nurseryTimezone);
  };

  // Parse URL parameters for filters and view mode (for navigation from other pages)
  useEffect(() => {
    // Only process URL parameters when we have a nursery loaded
    if (!currentNursery?.nursery_id) return;
    
    const urlParams = new URLSearchParams(location.search);
    
    // Check if we have navigation parameters
    const hasNavigationParams = urlParams.has('view') || 
                                urlParams.has('child_name') || 
                                urlParams.has('classroom_name') || 
                                urlParams.has('from_date') || 
                                urlParams.has('to_date');
    
    if (hasNavigationParams) {
      console.log('🔗 Processing URL parameters for navigation from Child Timeline or Daily Attendance');
      
      // Check if this is navigation from Child Timeline (has both child_name and classroom_name)
      const isFromTimeline = urlParams.has('child_name') && urlParams.has('classroom_name');
      if (isFromTimeline) {
        console.log('📅 Navigation detected from Child Timeline');
        setIsNavigatingFromTimeline(true);
      }
      
      // Set view mode
      const viewParam = urlParams.get('view');
      if (viewParam === 'individual' || viewParam === 'summary') {
        console.log('📊 Setting view mode to:', viewParam);
        setViewMode(viewParam);
      }
      
      // Build filters from URL parameters
      const urlFilters: Partial<FaceImageFilters> = {
        per_page: 20,
        page: 1
      };
      
      if (urlParams.get('child_name')) {
        urlFilters.child_name = urlParams.get('child_name')!;
        console.log('👤 Setting child filter:', urlFilters.child_name);
      }
      if (urlParams.get('classroom_name')) {
        urlFilters.classroom_name = urlParams.get('classroom_name')!;
        console.log('🏢 Setting classroom filter:', urlFilters.classroom_name);
      }
      if (urlParams.get('from_date')) {
        urlFilters.from_date = urlParams.get('from_date')!;
        console.log('📅 Setting from_date filter:', urlFilters.from_date);
      }
      if (urlParams.get('to_date')) {
        urlFilters.to_date = urlParams.get('to_date')!;
        console.log('📅 Setting to_date filter:', urlFilters.to_date);
      }
      
      console.log('🚀 Forcing fresh data loading with bypassCache=true');
      // Force fresh data loading when applying filters from URL parameters
      setBypassCache(true);
      
      console.log('🔄 Applying URL filters:', urlFilters);
      // Apply the filters immediately - no setTimeout needed
      setFilters(prevFilters => ({ ...prevFilters, ...urlFilters }));
      console.log('✅ URL filters applied successfully');
      
      // Also update form state to reflect the URL parameters in the form fields
      setFormState(prevFormState => ({
        ...prevFormState,
        childName: urlFilters.child_name || '',
        classroomName: urlFilters.classroom_name || '',
        fromDate: urlFilters.from_date || getTodayDate(nurseryTimezone),
        toDate: urlFilters.to_date || getTodayDate(nurseryTimezone)
      }));
      console.log('✅ Form state updated with URL parameters');
      
      // Mark URL parameters as processed
      setUrlParamsProcessed(true);
      console.log('✅ URL parameters processed and marked as complete');
      
      // Clear URL parameters after applying them to avoid reapplying on subsequent navigation
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      console.log('✅ URL parameters processed and cleared');
    }
  }, [location.search, currentNursery?.nursery_id, setFilters, setFormState, nurseryTimezone]);

  // Memoized values
  const paginatedSummaryData = useMemo(() => {
    const startIndex = (summaryPage - 1) * SUMMARY_ITEMS_PER_PAGE;
    const endIndex = startIndex + SUMMARY_ITEMS_PER_PAGE;
    return childrenSummary.slice(startIndex, endIndex);
  }, [childrenSummary, summaryPage]);

  // Check if we should show first/last records - NEW
  const shouldShowFirstLastRecords = useMemo(() => {
    const isFirstPage = (filters.page || 1) === 1;
    return viewMode === 'individual' && 
           isFirstPage && 
           filters.child_name && 
           filters.child_name.trim() !== '' &&
           !isNavigatingFromTimeline && // Don't show first/last records when navigating from timeline
           (firstLastRecords.first || firstLastRecords.last);
  }, [viewMode, filters.page, filters.child_name, firstLastRecords, isNavigatingFromTimeline]);

  const convertToSummaryFilters = useCallback((individualFilters: FaceImageFilters): ChildrenAggregateFilters => {
    return {
      child_name: individualFilters.child_name,
      classroom_name: individualFilters.classroom_name,
      camera_name: individualFilters.camera_name,
      camera_number: individualFilters.camera_number,
      image_file: individualFilters.image_file,
      model_setup: individualFilters.model_setup,
      from_date: individualFilters.from_date,
      to_date: individualFilters.to_date,
    };
  }, []);

  // Load classrooms
  const loadClassrooms = useCallback(async () => {
    if (!currentNursery?.nursery_id || classroomsLoading) return;

    setClassroomsLoading(true);
    try {
      const response = await classroomAPI.getClassrooms(currentNursery.nursery_id);
      setClassrooms(response.classrooms || []);
    } catch (err: any) {
      console.error('Error loading classrooms:', err);
    } finally {
      setClassroomsLoading(false);
    }
  }, [currentNursery?.nursery_id]);

  // Load children
  const loadChildren = useCallback(async () => {
    if (!currentNursery?.nursery_id || childrenLoading) return;

    setChildrenLoading(true);
    try {
      const response = await childAPI.getChildren(currentNursery.nursery_id, includeInactive);
      setChildren(response.children || []);
    } catch (err: any) {
      console.error('Error loading children:', err);
    } finally {
      setChildrenLoading(false);
    }
  }, [currentNursery?.nursery_id, includeInactive]);

  // Centralized data loading effect
  useEffect(() => {
    if (!currentNursery?.nursery_id) return;
    
    if (prevNurseryIdRef.current && currentNursery.nursery_id !== prevNurseryIdRef.current) {
      prevNurseryIdRef.current = currentNursery.nursery_id;
      clearFilters();
      return;
    }
    
    prevNurseryIdRef.current = currentNursery.nursery_id;
    
    // If we have URL parameters, wait for them to be processed
    const hasUrlParams = location.search.includes('view=') || 
                        location.search.includes('child_name=') || 
                        location.search.includes('classroom_name=') || 
                        location.search.includes('from_date=') || 
                        location.search.includes('to_date=');
    
    if (hasUrlParams && !urlParamsProcessed) {
      console.log('⏳ Waiting for URL parameters to be processed...');
      return;
    }

    console.log('📊 Data loading effect triggered with:', {
      viewMode,
      filters,
      bypassCache,
      currentPage,
      nurseryId: currentNursery.nursery_id,
      urlParamsProcessed
    });

    if (viewMode === 'summary') {
      console.log('📈 Loading summary data...');
      loadChildrenSummary(
        convertToSummaryFilters(filters),
        setChildrenSummary,
        setSummaryTotalRecords,
        setSummaryTotalPages,
        setSummaryPage,
        SUMMARY_ITEMS_PER_PAGE,
        bypassCache
      );
    } else {
      console.log('🔍 Loading individual records...', { filters, bypassCache });
      loadIndividualRecords(
        filters,
        setFaceImages,
        setCurrentPage,
        setTotalPages,
        setTotalRecords,
        bypassCache
      );
      
      // Load first/last records if conditions are met - NEW
      // Use filters.page instead of currentPage for more reliable state
      const isFirstPage = (filters.page || 1) === 1;
      if (isFirstPage && filters.child_name && filters.child_name.trim() !== '') {
        console.log('🎯 Loading first/last records for child:', filters.child_name);
        loadChildFirstLastRecords(
          filters.child_name,
          {
            classroom_name: filters.classroom_name,
            camera_name: filters.camera_name,
            camera_number: filters.camera_number,
            image_file: filters.image_file,
            model_setup: filters.model_setup,
            from_date: filters.from_date,
            to_date: filters.to_date,
          },
          setFirstLastRecords
        );
      } else {
        setFirstLastRecords({});
      }
    }
    
    // Note: bypassCache and isNavigatingFromTimeline resets are handled in separate effects
  }, [filters, viewMode, currentNursery?.nursery_id, bypassCache, isNavigatingFromTimeline, urlParamsProcessed, location.search, loadIndividualRecords, loadChildFirstLastRecords, loadChildrenSummary, convertToSummaryFilters, clearFilters, currentPage]);

  // Reset bypassCache after data loading to ensure normal caching behavior
  useEffect(() => {
    if (bypassCache) {
      console.log('🔄 Resetting bypassCache to false');
      setBypassCache(false);
    }
  }, [bypassCache]);

  // Reset navigation flag after data loading to allow normal first/last records behavior
  useEffect(() => {
    if (isNavigatingFromTimeline) {
      console.log('🔄 Resetting isNavigatingFromTimeline to false');
      setIsNavigatingFromTimeline(false);
    }
  }, [isNavigatingFromTimeline]);

  // Load classrooms and children when nursery changes
  useEffect(() => {
    loadClassrooms();
    loadChildren();
  }, [loadClassrooms, loadChildren]);

  const findJourneyChild = useCallback((childList: Child[]): Child | null => {
    const selectedName = filters.child_name?.trim();
    if (!selectedName) return null;

    const normalize = (value?: string | null) => (value || '').trim().toLowerCase();
    const getClassroomName = (child: Child) => (typeof child.classroom === 'string' ? child.classroom : child.classroom?.name) || '';
    const recordChildId = firstLastRecords.first?.child_id || firstLastRecords.last?.child_id;
    const selectedClassroom = firstLastRecords.first?.classroom_name || firstLastRecords.last?.classroom_name || filters.classroom_name;

    const idMatches = recordChildId
      ? childList.filter(child => child.child_id && String(child.child_id) === String(recordChildId))
      : [];
    const nameMatches = childList.filter(child => normalize(child.name) === normalize(selectedName));
    const candidatePool = idMatches.length > 0 ? idMatches : nameMatches;
    const classroomMatches = selectedClassroom
      ? candidatePool.filter(child => normalize(getClassroomName(child)) === normalize(selectedClassroom))
      : candidatePool;
    const candidates = classroomMatches.length > 0 ? classroomMatches : candidatePool;

    return candidates.length === 1 ? candidates[0] : null;
  }, [filters.child_name, filters.classroom_name, firstLastRecords.first?.child_id, firstLastRecords.first?.classroom_name, firstLastRecords.last?.child_id, firstLastRecords.last?.classroom_name]);

  const handleOpenJourneyModal = useCallback(async () => {
    setJourneyError(null);

    const selectedName = filters.child_name?.trim();
    if (!selectedName) {
      setJourneyError(t('playClipSelectChild'));
      return;
    }

    let resolvedChild = findJourneyChild(children);

    if (!resolvedChild && currentNursery?.nursery_id) {
      setJourneyResolving(true);
      try {
        const response = await childAPI.getChildren(currentNursery.nursery_id, true);
        resolvedChild = findJourneyChild(response.children || []);
      } catch (err) {
        console.error('Error resolving child for detection journey:', err);
      } finally {
        setJourneyResolving(false);
      }
    }

    if (!resolvedChild) {
      setJourneyError(t('playClipChildNotResolved', { child: selectedName }));
      return;
    }

    setSelectedJourneyChild({ childId: resolvedChild.id, childName: resolvedChild.name });
    setShowJourneyModal(true);
  }, [children, currentNursery?.nursery_id, filters.child_name, findJourneyChild, t]);

  const handleHideJourneyModal = useCallback(() => {
    setShowJourneyModal(false);
    setSelectedJourneyChild(null);
  }, []);

  // Event handlers
  const handleFilterSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setJourneyError(null);
    // Set bypassCache to true to force fresh API calls when Apply Filters is clicked
    setBypassCache(true);
    applyFormFilters();
  }, [applyFormFilters]);

  const handleClearFilters = useCallback(() => {
    setJourneyError(null);
    clearFilters();
    setSummaryPage(1);
  }, [clearFilters]);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    setExpandedChildren(new Set());
    
    if (mode === 'summary') {
      setSummaryPage(1);
    } else {
      updateFiltersWithPageReset({ page: 1 });
    }
  }, [updateFiltersWithPageReset]);

  const handlePageChange = useCallback((page: number) => {
    if (viewMode === 'summary') {
      setSummaryPage(page);
    } else {
      updateFilters({ page });
    }
  }, [viewMode, updateFilters]);

  const handleViewChildDetails = useCallback((childName: string) => {
    const trimmedName = childName.trim();
    updateFiltersWithPageReset({ child_name: trimmedName, page: 1 });
    setViewMode('individual');
    setSummaryPage(1);
  }, [updateFiltersWithPageReset]);

  const handleToggleChildExpansion = useCallback((childId: string) => {
    setExpandedChildren(prev => {
      const newSet = new Set(prev);
      if (newSet.has(childId)) {
        newSet.delete(childId);
      } else {
        newSet.add(childId);
      }
      return newSet;
    });
  }, []);

  // Utility functions
  const getImageUrl = (record: FaceImageRecord): string => {
    return faceImageAPI.getImageUrl(
      currentNursery?.nursery_id || '',
      record.camera_number,
      record.image_file
    );
  };


  const parseShareParams = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has('share') || urlParams.get('share') !== 'true') {
      return null;
    }
    
    return {
      child_id: urlParams.get('child_id'),
      log_time: urlParams.get('log_time'),
      camera_number: urlParams.get('camera_number'),
      image_file: urlParams.get('image_file'),
      nursery_id: urlParams.get('nursery_id')
    };
  }, []);

  const fetchSharedRecord = useCallback(async (shareParams: any) => {
    if (!currentNursery?.nursery_id || !shareParams) return;
    
    // Verify the nursery matches
    if (shareParams.nursery_id !== currentNursery.nursery_id) {
      setShareError(t('shareWrongNursery'));
      return;
    }

    setLoadingSharedRecord(true);
    setShareError(null);

    try {
      // Create filters to find the specific record
      const searchFilters: FaceImageFilters = {
        image_file: shareParams.image_file,
        camera_number: shareParams.camera_number,
        per_page: 1,
        page: 1
      };

      const response = await faceImageAPI.getFaceImages(currentNursery.nursery_id, searchFilters);
      
      if (response.success && response.data.length > 0) {
        // Find the exact record by matching log_time
        const exactRecord = response.data.find(record => 
          record.log_time === shareParams.log_time
        );
        
        if (exactRecord) {
          setSelectedImage(exactRecord);
          setShowImageModal(true);
          setViewMode('individual');
          
          // Update URL to remove share parameters but keep the record visible
          const newUrl = window.location.pathname + '?view=individual';
          window.history.replaceState({}, '', newUrl);
        } else {
          setShareError(t('shareNotFound'));
        }
      } else {
        setShareError(t('shareNotFound'));
      }
    } catch (error: any) {
      console.error('Error fetching shared record:', error);
      setShareError(t('shareLoadFailed'));
    } finally {
      setLoadingSharedRecord(false);
    }
  }, [currentNursery]);


  // Handle shared links when component mounts
  useEffect(() => {
    const shareParams = parseShareParams();
    if (shareParams && currentNursery) {
      fetchSharedRecord(shareParams);
    }
  }, [currentNursery, parseShareParams, fetchSharedRecord]);

  // Modal handlers
  const handleImageClick = useCallback((record: FaceImageRecord) => {
    setSelectedImage(record);
    setShowImageModal(true);
  }, []);

  const handleCloseImageModal = useCallback(() => {
    setShowImageModal(false);
    setSelectedImage(null);
    setShareError(null);
    setLoadingSharedRecord(false);
  }, []);

  const handleImageError = useCallback((imageUrl: string) => {
    setFailedImages(prev => new Set(prev).add(imageUrl));
  }, []);

  // CSV Export functions
  const downloadCSV = useCallback((blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  const formatDateForFilename = useCallback((date?: string) => {
    if (!date) return '';
    return date.replace(/-/g, '');
  }, []);

  const generateFilename = useCallback((type: 'individual' | 'summary') => {
    const nurseryName = (currentNursery?.description || 'nursery').replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const nurseryTimezone = currentNursery?.timezone || 'UTC';
    const datePart = getTodayDateInNurseryTimezone(nurseryTimezone).replace(/-/g, '');
    const timestamp = new Date().toISOString().slice(11, 19).replace(/:/g, '');
    const fromDateStr = formatDateForFilename(filters.from_date);
    const toDateStr = formatDateForFilename(filters.to_date);
    const dateRange = fromDateStr && toDateStr ? `_${fromDateStr}_to_${toDateStr}` : 
                     fromDateStr ? `_from_${fromDateStr}` : 
                     toDateStr ? `_to_${toDateStr}` : '';
    
    return `${nurseryName}_${type}_records${dateRange}_${datePart}_${timestamp}.csv`;
  }, [currentNursery?.description, currentNursery?.timezone, filters.from_date, filters.to_date, formatDateForFilename]);

  const handleExportIndividualCSV = useCallback(async () => {
    if (!currentNursery?.nursery_id || csvExporting) return;

    setCsvExporting(true);
    try {
      // Remove pagination filters for CSV export to get all records
      const csvFilters = { ...filters };
      delete csvFilters.page;
      delete csvFilters.per_page;

      const blob = await faceImageAPI.getFaceImagesCSV(currentNursery.nursery_id, csvFilters, formState.timezone);
      const filename = generateFilename('individual');
      downloadCSV(blob, filename);
    } catch (error: any) {
      console.error('Error exporting individual records CSV:', error);
      // You could add a toast notification here
      alert(t('exportFailed'));
    } finally {
      setCsvExporting(false);
    }
  }, [currentNursery?.nursery_id, filters, formState.timezone, csvExporting, generateFilename, downloadCSV, t]);

  const handleExportSummaryCSV = useCallback(async () => {
    if (!currentNursery?.nursery_id || csvExporting) return;

    setCsvExporting(true);
    try {
      const summaryFilters = convertToSummaryFilters(filters);
      const blob = await faceImageAPI.getChildrenAggregateCSV(currentNursery.nursery_id, summaryFilters, formState.timezone);
      const filename = generateFilename('summary');
      downloadCSV(blob, filename);
    } catch (error: any) {
      console.error('Error exporting summary CSV:', error);
      // You could add a toast notification here
      alert(t('exportFailed'));
    } finally {
      setCsvExporting(false);
    }
  }, [currentNursery?.nursery_id, filters, formState.timezone, csvExporting, convertToSummaryFilters, generateFilename, downloadCSV, t]);

  // Pagination renderers
  const renderIndividualPagination = useCallback(() => {
    if (totalPages <= 1) return null;
    
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
      items.push(
        <Pagination.Item
          key={`individual-${pageNum}`}
          active={pageNum === currentPage}
          onClick={() => handlePageChange(pageNum)}
        >
          {pageNum}
        </Pagination.Item>
      );
    }
    
    return (
      <Pagination>
        <Pagination.First 
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev 
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        />
        {items}
        <Pagination.Next 
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last 
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  }, [currentPage, totalPages, handlePageChange]);

  const renderSummaryPagination = useCallback(() => {
    if (summaryTotalPages <= 1) return null;
    
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, summaryPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(summaryTotalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
      items.push(
        <Pagination.Item
          key={`summary-${pageNum}`}
          active={pageNum === summaryPage}
          onClick={() => setSummaryPage(pageNum)}
        >
          {pageNum}
        </Pagination.Item>
      );
    }
    
    return (
      <Pagination>
        <Pagination.First 
          onClick={() => setSummaryPage(1)}
          disabled={summaryPage === 1}
        />
        <Pagination.Prev 
          onClick={() => setSummaryPage(summaryPage - 1)}
          disabled={summaryPage === 1}
        />
        {items}
        <Pagination.Next 
          onClick={() => setSummaryPage(summaryPage + 1)}
          disabled={summaryPage === summaryTotalPages}
        />
        <Pagination.Last 
          onClick={() => setSummaryPage(summaryTotalPages)}
          disabled={summaryPage === summaryTotalPages}
        />
      </Pagination>
    );
  }, [summaryPage, summaryTotalPages]);

  if (!currentNursery) {
    return (
      <Layout title={t('title')}>
        <Container fluid>
          <Alert variant="warning">
            {t('selectNursery')}
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2 className="fw-bold mb-0">{t('heading')}</h2>
            <p className="text-muted mb-0">{t('subtitle')}</p>
          </Col>
        </Row>

        <ScopeIndicator resource={viewMode === 'individual' ? 'facesImages' : 'childrenAggregate'} />

        {/* View Mode Tabs */}
        <Card className="mb-4">
          <Card.Header className="border-0 pb-0">
            <Nav variant="tabs" className="border-bottom-0">
              <Nav.Item>
                <Nav.Link
                  active={viewMode === 'summary'}
                  onClick={() => handleViewModeChange('summary')}
                  className={`cursor-pointer ${viewMode === 'summary' ? 'fw-semibold' : ''}`}
                >
                  📊 {t('tabSummary')}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={viewMode === 'individual'}
                  onClick={() => handleViewModeChange('individual')}
                  className={`cursor-pointer ${viewMode === 'individual' ? 'fw-semibold' : ''}`}
                >
                  🔍 {t('tabIndividual')}
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          
          <Card.Body className="pt-3">
            <div className="text-muted">
              {viewMode === 'summary' ? (
                <span>{t('summaryHint')}</span>
              ) : (
                <span>{t('individualHint')}</span>
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Filters Section */}
        <Card className="mb-4 filter-card">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t('filtersTitle')}</h5>
              <small className="text-muted">
                📊 {t('csvNote')}
              </small>
            </div>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleFilterSubmit}>
              <Row>
                <Col lg={3} md={6} className="mb-3">
                                      <Form.Group>
                      <Form.Label>{t('childName')}</Form.Label>
                      <Form.Select
                        value={formState.childName}
                        onChange={(e) => updateFormState('childName', e.target.value)}
                        disabled={childrenLoading}
                      >
                        <option value="">{tc('labels.allChildren')}</option>
                        {children.map((child) => (
                          <option 
                            key={child.id} 
                            value={child.name}
                            style={child.is_active_today === false ? { color: '#6c757d' } : {}}
                          >
                            {child.name}{child.is_active_today === false ? ` (${tc('labels.inactive')})` : ''}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Check 
                        type="switch"
                        id="include-inactive-switch"
                        label={tc('labels.includeInactive')}
                        className="mt-2 small text-muted"
                        checked={includeInactive}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIncludeInactive(checked);
                          if (!checked) {
                            updateFormState('childName', '');
                            if (filters.child_name) {
                              setFilters(prev => ({ ...prev, child_name: undefined, page: 1 }));
                            }
                          }
                        }}
                        disabled={childrenLoading}
                      />
                    </Form.Group>
                </Col>
                
                <Col lg={3} md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{tc('labels.classroom')}</Form.Label>
                    <Form.Select
                      value={formState.classroomName}
                      onChange={(e) => updateFormState('classroomName', e.target.value)}
                      disabled={classroomsLoading}
                    >
                      <option value="">{tc('labels.allClassrooms')}</option>
                      {classrooms.map((classroom) => (
                        <option key={classroom.id} value={classroom.name}>
                          {classroom.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col lg={3} md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{tc('labels.fromDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={formState.fromDate}
                      onChange={(e) => updateFormState('fromDate', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                
                <Col lg={3} md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{tc('labels.toDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={formState.toDate}
                      onChange={(e) => updateFormState('toDate', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>


              
              <Row>
                <Col>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Button type="submit" variant="primary" disabled={loading}>
                      {loading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                          {tc('loading.filtering')}
                        </>
                      ) : (
                        tc('buttons.applyFilters')
                      )}
                    </Button>
                    <Button type="button" variant="outline-secondary" onClick={handleClearFilters}>
                      {tc('buttons.clearFilters')}
                    </Button>
                    
                    <div className="ms-auto d-flex gap-2">
                      {viewMode === 'individual' ? (
                        <Button 
                          variant="success" 
                          size="sm" 
                          onClick={handleExportIndividualCSV}
                          disabled={csvExporting || loading || faceImages.length === 0}
                        >
                          {csvExporting ? (
                            <>
                              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                              {tc('loading.exporting')}
                            </>
                          ) : (
                            <>
                              📊 {t('exportIndividualCsv')}
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button 
                          variant="success" 
                          size="sm" 
                          onClick={handleExportSummaryCSV}
                          disabled={csvExporting || loading || childrenSummary.length === 0}
                        >
                          {csvExporting ? (
                            <>
                              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                              {tc('loading.exporting')}
                            </>
                          ) : (
                            <>
                              📋 {t('exportSummaryCsv')}
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" className="mb-4">
            <strong>{tc('errors.error')}</strong> {error}
          </Alert>
        )}

        {/* Share Error Alert */}
        {shareError && (
          <Alert variant="warning" className="mb-4" dismissible onClose={() => setShareError(null)}>
            <strong>{t('shareError')}</strong> {shareError}
          </Alert>
        )}

        {journeyError && (
          <Alert variant="warning" className="mb-4" dismissible onClose={() => setJourneyError(null)}>
            {journeyError}
          </Alert>
        )}

        {/* AI Chat Assistant */}
        <ReportChatAssistant 
          reportData={viewMode === 'summary' ? childrenSummary : faceImages}
          viewMode={viewMode}
          isLoading={loading}
          totalRecords={viewMode === 'summary' ? summaryTotalRecords : totalRecords}
          currentFilters={filters}
          nurseryId={currentNursery?.nursery_id}
        />

        {/* Results Summary */}
        {!loading && (
          <Row className="mb-3">
            <Col>
              {viewMode === 'individual' ? (
                <p className="text-muted mb-0">
                  {t('showingRecords', { shown: faceImages.length, total: totalRecords })}
                  {totalPages > 1 && ` ${t('page', { page: currentPage, pages: totalPages })}`}
                </p>
              ) : (
                <p className="text-muted mb-0">
                  {t('showingChildren', { shown: paginatedSummaryData.length, total: summaryTotalRecords })}
                  {summaryTotalPages > 1 && ` ${t('page', { page: summaryPage, pages: summaryTotalPages })}`}
                </p>
              )}
            </Col>
          </Row>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-5 loading-container">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2 text-muted">
              {viewMode === 'summary' 
                ? t('loadingSummary')
                : t('loadingIndividual')
              }
            </p>
          </div>
        )}

        {/* First and Last Records Section - NEW */}
        {shouldShowFirstLastRecords && (
          <Card className="mb-4" style={{ backgroundColor: '#f8f9fa', maxHeight: '790px', overflow: 'hidden' }}>
            <Card.Header className="bg-primary text-white py-2">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">📅 {t('dailyAttendance', { child: filters.child_name })}</h6>
                <div className="d-flex align-items-center gap-3">
                  <Button 
                    variant="light" 
                    size="sm" 
                    className="py-0 px-2 fw-semibold d-flex align-items-center gap-1"
                    style={{ fontSize: '0.75rem', height: '24px' }}
                    onClick={handleOpenJourneyModal}
                    disabled={journeyResolving || childrenLoading}
                  >
                    {journeyResolving ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <i className="bi bi-play-circle-fill text-primary"></i>
                    )}
                    {t('playClip')}
                  </Button>
                  <small>{t('firstLatest')}</small>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-2" style={{ maxHeight: '697px' }}>
              <Row className="g-2">
                {firstLastRecords.last && firstLastRecords.first?.log_time !== firstLastRecords.last?.log_time && (
                  <Col md={6} className="d-flex flex-column">
                    <div className="text-center mb-1">
                      <Badge bg="success" className="mb-1">
                        🕒 {t('latestBadge')}
                      </Badge>
                    </div>
                    <Card 
                      className="flex-grow-1 shadow-sm border-2 border-success" 
                      style={{ cursor: 'pointer', maxHeight: '568px', overflow: 'hidden' }}
                      onClick={() => handleImageClick(firstLastRecords.last!)}
                    >
                      <div className="position-relative">
                        <div className="image-container" style={{ height: '283px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff' }}>
                          {(() => {
                            const imageUrl = getImageUrl(firstLastRecords.last!);
                            const isImageFailed = failedImages.has(imageUrl);
                            return imageUrl && !isImageFailed ? (
                              <img
                                src={imageUrl}
                                alt={`${firstLastRecords.last!.child_name} latest detection`}
                                style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }}
                                onError={() => handleImageError(imageUrl)}
                              />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center h-100 bg-light text-muted">
                                <div className="text-center">
                                  <i className="bi bi-image mb-1" style={{ fontSize: '1.2rem' }}></i>
                                  <br />
                                  <small>{t('noImage')}</small>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <Card.Body className="p-2" style={{ fontSize: '0.8rem' }}>
                        <div className="mb-1">
                          <small className="text-muted" style={{ fontSize: '0.7rem' }}>{t('detectionTime')}</small>
                          <div className="fw-medium" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>{formatDateTime(firstLastRecords.last.log_time)}</div>
                        </div>
                        <div className="mb-1">
                          <small className="text-muted" style={{ fontSize: '0.7rem' }}>{t('cameraLabel')}</small>
                          <div style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>{firstLastRecords.last.camera_name} ({firstLastRecords.last.camera_number})</div>
                        </div>
                        <div className="mb-0">
                          <small className="text-muted" style={{ fontSize: '0.7rem' }}>{t('classroomLabel')}</small>
                          <div style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>{firstLastRecords.last.classroom_name}</div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
                
                {firstLastRecords.first && (
                  <Col md={6} className="d-flex flex-column">
                    <div className="text-center mb-1">
                      <Badge bg="info" className="mb-1">
                        🕐 {t('firstBadge')}
                      </Badge>
                    </div>
                    <Card 
                      className="flex-grow-1 shadow-sm border-2 border-info" 
                      style={{ cursor: 'pointer', maxHeight: '568px', overflow: 'hidden' }}
                      onClick={() => handleImageClick(firstLastRecords.first!)}
                    >
                      <div className="position-relative">
                        <div className="image-container" style={{ height: '283px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff' }}>
                          {(() => {
                            const imageUrl = getImageUrl(firstLastRecords.first!);
                            const isImageFailed = failedImages.has(imageUrl);
                            return imageUrl && !isImageFailed ? (
                              <img
                                src={imageUrl}
                                alt={`${firstLastRecords.first!.child_name} first detection`}
                                style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }}
                                onError={() => handleImageError(imageUrl)}
                              />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center h-100 bg-light text-muted">
                                <div className="text-center">
                                  <i className="bi bi-image mb-1" style={{ fontSize: '1.2rem' }}></i>
                                  <br />
                                  <small>{t('noImage')}</small>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <Card.Body className="p-2" style={{ fontSize: '0.8rem' }}>
                        <div className="mb-1">
                          <small className="text-muted" style={{ fontSize: '0.7rem' }}>{t('detectionTime')}</small>
                          <div className="fw-medium" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>{formatDateTime(firstLastRecords.first.log_time)}</div>
                        </div>
                        <div className="mb-1">
                          <small className="text-muted" style={{ fontSize: '0.7rem' }}>{t('cameraLabel')}</small>
                          <div style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>{firstLastRecords.first.camera_name} ({firstLastRecords.first.camera_number})</div>
                        </div>
                        <div className="mb-0">
                          <small className="text-muted" style={{ fontSize: '0.7rem' }}>{t('classroomLabel')}</small>
                          <div style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>{firstLastRecords.first.classroom_name}</div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
                
                {firstLastRecords.first && !firstLastRecords.last && (
                  <Col md={6} className="d-flex align-items-center justify-content-center">
                    <div className="text-center text-muted">
                      <i className="bi bi-info-circle mb-2" style={{ fontSize: '1.5rem' }}></i>
                      <p className="mb-0 small">{t('onlyOneRecord')}</p>
                    </div>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Face Images Grid - Individual View */}
        {viewMode === 'individual' && !loading && faceImages.length > 0 && (
          <>
            <Row className="face-image-grid">
              {faceImages.map((record, index) => {
                const imageUrl = getImageUrl(record);
                const isImageFailed = failedImages.has(imageUrl);
                return (
                  <Col key={`${record.child_id}-${record.log_time}-${index}`} xl={3} lg={4} md={6} className="mb-4">
                    <Card 
                      className="h-100 shadow-sm face-image-card" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleImageClick(record)}
                    >
                      <div className="position-relative">
                        <div className="image-container" style={{ height: '250px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
                          {imageUrl && !isImageFailed ? (
                            <img
                              src={imageUrl}
                              alt={`${record.child_name} detection`}
                              style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }}
                              onError={() => handleImageError(imageUrl)}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center h-100 bg-light text-muted">
                              <div className="text-center">
                                <i className="bi bi-image mb-2" style={{ fontSize: '2rem' }}></i>
                                <br />
                                <small>{t('imageNotAvailable')}</small>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <Card.Body>
                        <div className="mb-2">
                          <strong className="text-truncate d-block">{record.child_name}</strong>
                          <div className="d-flex align-items-center gap-2">
                            <small className="text-muted">{record.child_id}</small>
                            <div className="d-flex gap-1">
                              {record.is_vacation && <Badge bg="info" style={{ fontSize: '0.65rem' }}>{t('vacation')}</Badge>}
                              {record.is_holiday && <Badge bg="success" style={{ fontSize: '0.65rem' }}>{t('holiday')}</Badge>}
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-2">
                          <small className="text-muted d-block">{t('classroomLabel')}</small>
                          <span className="small">{record.classroom_name}</span>
                        </div>
                        
                        <div className="mb-2">
                          <small className="text-muted d-block">{t('cameraLabel')}</small>
                          <span className="small">{record.camera_name} ({record.camera_number})</span>
                        </div>
                        
                        <div className="mb-2">
                          <small className="text-muted d-block">{t('detectionTime')}</small>
                          <span className="small">{formatDateTime(record.log_time)}</span>
                        </div>
                        
                        <div className="mb-0">
                          <small className="text-muted d-block">{t('coordinatesLabel')}</small>
                          <span className="coordinates-text">
                            ({record.coordinates.x1}, {record.coordinates.y1}) → ({record.coordinates.x2}, {record.coordinates.y2})
                          </span>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* Individual View Pagination - FIXED */}
            {totalPages > 1 && (
              <Row className="mt-4">
                <Col className="d-flex justify-content-center">
                  {renderIndividualPagination()}
                </Col>
              </Row>
            )}
          </>
        )}

        {/* Child Summary Grid */}
        {viewMode === 'summary' && !loading && childrenSummary.length > 0 && (
          <>
            <Row className="child-summary-grid">
              {paginatedSummaryData.map((child) => (
                <Col key={child.child_id} lg={6} xl={4} className="mb-4">
                  <Card className="h-100 shadow-sm child-summary-card">
                    <Card.Header className="bg-light">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="me-2" style={{ minWidth: 0 }}>
                          <strong style={{ wordBreak: 'break-word' }}>{child.child_name}</strong>
                          <br />
                          <small className="text-muted">{child.child_id}</small>
                          {isHolidayAggregate(child) && (
                            <div className="mt-1">
                              <Badge bg="success">{t('holiday')}</Badge>
                            </div>
                          )}
                        </div>
                        <Badge bg="primary" className="ms-2 flex-shrink-0 align-self-center" style={{ whiteSpace: 'nowrap' }}>
                          {typeof child.total_detections === 'string' && child.total_detections === 'vacation' ? (
                            <>🏖️ {t('onVacation')}</>
                          ) : (
                            <>{t('detectionsCount', { count: child.total_detections })}</>
                          )}
                        </Badge>
                      </div>
                    </Card.Header>
                    
                    <Card.Body>
                      <Row className="mb-3">
                        <Col sm={6}>
                          <div className="mb-2">
                            <small className="text-muted d-block">{t('classroomLabel')}</small>
                            <span className="fw-medium">{child.classroom_name}</span>
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="mb-2">
                            <small className="text-muted d-block">{t('camerasUsed')}</small>
                            <span className="fw-medium">{child.unique_cameras}</span>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted d-block">{t('mostUsed')}</small>
                            <span className="fw-medium text-truncate d-block">{child.most_used_camera}</span>
                          </div>
                        </Col>
                      </Row>
                      
                      <div className="mb-3">
                        <small className="text-muted d-block">{t('activityPeriod')}</small>
                        <div className="small">
                          <strong>{t('first')}</strong> {formatDateTime(child.first_detection)}<br />
                          <strong>{t('last')}</strong> {formatDateTime(child.last_detection)}
                        </div>
                      </div>
                      
                      <div className="d-flex justify-content-between align-items-center">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleChildExpansion(child.child_id);
                          }}
                        >
                          {expandedChildren.has(child.child_id) ? t('hideRecent') : t('showRecent')}
                        </Button>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewChildDetails(child.child_name);
                          }}
                        >
                          {tc('buttons.viewDetails')}
                        </Button>
                      </div>
                      
                      {expandedChildren.has(child.child_id) && (
                        <div className="mt-3 pt-3 border-top">
                          <small className="text-muted d-block mb-2">{t('recentDetections')}</small>
                          {isHolidayAggregate(child) ? (
                            <div className="text-center py-2" style={{ color: '#009688' }}>
                              <p className="mb-0 mt-1 small fw-semibold">{t('holidayMessage')}</p>
                            </div>
                          ) : typeof child.recent_detections === 'string' && child.recent_detections === 'vacation' ? (
                            <div className="text-center py-2 text-primary">
                              <span className="fs-5">🏖️</span>
                              <p className="mb-0 mt-1 small fw-semibold">{t('vacationMessage')}</p>
                            </div>
                          ) : Array.isArray(child.recent_detections) && child.recent_detections.length > 0 ? (
                            child.recent_detections.map((detection: any, idx: number) => (
                            <div key={idx} className="d-flex justify-content-between align-items-center py-1 small">
                              <span>{formatDateTime(detection.log_time)}</span>
                              <span className="text-muted">{detection.camera_name}</span>
                            </div>
                            ))
                          ) : (
                            <div className="text-center py-2 text-muted">
                              <small>{t('noRecentDetections')}</small>
                            </div>
                          )}
                          {Array.isArray(child.recent_detections) && child.recent_detections.length === 5 && (
                            <div className="text-center mt-2">
                              <Button 
                                size="sm" 
                                variant="link" 
                                className="p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewChildDetails(child.child_name);
                                }}
                              >
                                {typeof child.total_detections === 'string' && child.total_detections === 'vacation' ? (
                                  <>{t('viewVacationDetails')}</>
                                ) : (
                                  <>{t('viewAllDetections', { count: child.total_detections })}</>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Summary View Pagination - FIXED */}
            {summaryTotalPages > 1 && (
              <Row className="mt-4">
                <Col className="d-flex justify-content-center">
                  {renderSummaryPagination()}
                </Col>
              </Row>
            )}
          </>
        )}

        {/* Empty State */}
        {!loading && 
         ((viewMode === 'individual' && faceImages.length === 0) || 
          (viewMode === 'summary' && childrenSummary.length === 0)) && 
         !error && (
          <Card className="text-center py-5 empty-state">
            <Card.Body>
              <h4 className="text-muted mb-3">
                {viewMode === 'summary' ? t('noChildData') : t('noFaceImages')}
              </h4>
              <p className="text-muted mb-0">
                {viewMode === 'summary' 
                  ? t('noSummaryMatch')
                  : t('noIndividualMatch')
                }
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Image Modal */}
        <Modal 
          show={showImageModal} 
          onHide={handleCloseImageModal} 
          size="lg" 
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('modalTitle', { child: selectedImage?.child_name })}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {loadingSharedRecord && (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="primary" className="me-3" />
                <span>{t('loadingSharedRecord')}</span>
              </div>
            )}
            
            {shareError && (
              <Alert variant="danger" className="m-3">
                <strong>{tc('errors.error')}</strong> {shareError}
              </Alert>
            )}
            
            {selectedImage && !loadingSharedRecord && !shareError && (() => {
              const modalImageUrl = getImageUrl(selectedImage);
              const isModalImageFailed = failedImages.has(modalImageUrl);
              return (
                <div className="image-container">
                  <div className="position-relative w-100">
                    <div className="d-flex justify-content-center w-100">
                      {modalImageUrl && !isModalImageFailed ? (
                        <img
                          src={modalImageUrl}
                          alt={`${selectedImage.child_name} detection`}
                          className="img-fluid w-100"
                          style={{ maxHeight: '70vh', width: '100%' }}
                        />
                      ) : (
                        <div className="text-center p-5">
                          <i className="bi bi-image mb-3" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                          <p className="text-muted">{t('imageNotAvailable')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="p-3 bg-light reports-modal-details">
                    <Row>
                      <Col md={6}>
                        <div className="mb-2">
                          <strong>{t('modalChild')}</strong> {selectedImage.child_name} ({selectedImage.child_id})
                        </div>
                        <div className="mb-2">
                          <strong>{t('modalClassroom')}</strong> {selectedImage.classroom_name}
                        </div>
                        <div className="mb-2">
                          <strong>{t('modalCamera')}</strong> {selectedImage.camera_name} ({selectedImage.camera_number})
                        </div>
                        <div className="mb-2">
                          <strong>{t('modalCoordinates')}</strong>
                          <br />
                          <span className="coordinates-text">
                            ({selectedImage.coordinates.x1}, {selectedImage.coordinates.y1}) → ({selectedImage.coordinates.x2}, {selectedImage.coordinates.y2})
                          </span>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-2">
                          <strong>{t('modalDetectionTime')}</strong> {formatDateTime(selectedImage.log_time)}
                        </div>
                        <div className="mb-2">
                          <strong>{t('modalModelSetup')}</strong> {selectedImage.model_setup}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              );
            })()}
          </Modal.Body>
        </Modal>

        <DetectionJourneyModal
          show={showJourneyModal}
          onHide={handleHideJourneyModal}
          childId={selectedJourneyChild?.childId ?? null}
          childName={selectedJourneyChild?.childName || filters.child_name || ''}
          fromDate={filters.from_date || ''}
          toDate={filters.to_date || ''}
          nurseryId={currentNursery.nursery_id}
          nurseryTimezone={nurseryTimezone}
        />
      </Container>
    </Layout>
  );
};

export default ReportsComponent; 
